// missionsquad-web/src/composables/useStreamBuffer.ts

export interface StreamBufferOptions {
  /** Interval in ms between buffer flushes. Default: 40 */
  flushIntervalMs?: number
  /** Idle timeout in ms to force a flush. Default: 1000 */
  idleFlushMs?: number
}

export function useStreamBuffer(
  onFlush: (bufferedContent: string) => void,
  options: StreamBufferOptions = {}
) {
  const flushIntervalMs = options.flushIntervalMs ?? 40
  const idleFlushMs = options.idleFlushMs ?? 1000

  let buffer = ''
  let flushTimer: ReturnType<typeof setTimeout> | null = null
  let idleTimer: ReturnType<typeof setTimeout> | null = null

  function append(chunk: string): void {
    buffer += chunk

    // Reset idle timer on every append
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
    }
    idleTimer = setTimeout(flush, idleFlushMs)

    // Schedule a throttled flush if not already scheduled
    if (flushTimer === null) {
      flushTimer = setTimeout(flush, flushIntervalMs)
    }
  }

  function flush(): void {
    if (flushTimer !== null) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
    if (!buffer) return

    const content = buffer
    buffer = ''
    onFlush(content)
  }

  function reset(): void {
    buffer = ''
    if (flushTimer !== null) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }

  return { append, flush, reset }
}
